import React from "react";
import Header from "../components/layout/header/Header";
import Footer from "../components/layout/footer/Footer";
import "../App.css";
import "../components/layout/privacy and policy/privacyPolicy.css";
import { Helmet } from "react-helmet-async";
const pageTitle = "Privacy Policy | AIGIO";
const pageDescription = "Learn how we collect, use, and protect your personal information.";
type Block =
  | { type: "p"; text: string }
  | { type: "list"; items: string[] };

type Section = {
  id: string;
  title: string;
  number?: string;
  blocks: Block[];
};

const LAST_UPDATED = "01 July 2026";

const SECTIONS: Section[] = [
  {
    id: "information-we-collect",
    title: "Information we collect",
    number: "1",
    blocks: [
      {
        type: "p",
        text: "Information you provide",
      },
      {
        type: "list",
        items: [
          "Account data: your name, email address, and password (stored only as a secure hash, never in plain text).",
          "Brand inputs: the intake answers, industry, brand details, products, and any content you enter to generate outputs.",
          "Support communications: messages you send us.",
        ],
      },
      {
        type: "p",
        text: "Information collected automatically",
      },
      {
        type: "list",
        items: [
          "Usage data and logs needed to operate the Service (actions taken, credits used, timestamps).",
          "Standard technical data such as IP address, browser type, and approximate device information.",
          "Analytics data collected via Google Analytics and cookies see our Cookie Policy.",
        ],
      },
      {
        type: "p",
        text: "Payment information",
      },
      {
        type: "p",
        text: "Payments are processed by Razorpay. Your card or banking details are handled directly by the payment gateway under their security standards.",
      },
      {
        type: "p",
        text: "We do not store your full card number, CVV, or banking credentials on our servers.",
      },
    ],
  },
  {
    id: "how-we-use",
    title: "How we use your information",
    number: "2",
    blocks: [
      {
        type: "list",
        items: [
          "To provide, operate, and maintain the Service and your account.",
          "To generate the Al outputs you request.",
          "To process subscriptions and payments, manage credits, and handle refunds.",
          "To respond to your support requests.",
          "To send service and, where you have not opted out, marketing emails (see Section 7).",
          "To monitor, secure, analyse, and improve the Service.",
          "To comply with legal obligations.",
        ],
      },
    ],
  },
  {
    id: "ai-processing",
    title: "Al processing",
    number: "3",
    blocks: [
      {
        type: "p",
        text: "To generate outputs, the relevant inputs are sent to trusted third-party Al processing providers solely to produce your requested output.",
      },
      {
        type: "p",
        text: "We send only what is needed for that purpose. We do not sell your brand inputs, and we do not use them to publicly identify you.",
      },
    ],
  },
  {
    id: "how-we-share",
    title: "How we share information",
    number: "4",
    blocks: [
      {
        type: "p",
        text: "We do not sell your personal information. We share it only with:",
      },
      {
        type: "list",
        items: [
          "Service providers that help us run the Service our payment gateway (Razorpay), cloud hosting (Amazon Web Services), analytics (Google Analytics), and Al processing providers - bound to use it only for that purpose.",
          "Legal and safety recipients where required by law or to protect rights, safety, and security.",
          "Business transfers, if we are involved in a merger, acquisition, or asset sale, subject to this policy.",
        ],
      },
    ],
  },
  {
    id: "data-storage",
    title: "Data storage and international transfer",
    number: "5",
    blocks: [
      {
        type: "p",
        text: "Your data is stored on Amazon Web Services (AWS) cloud infrastructure.",
      },
      {
        type: "p",
        text: "Because we serve customers in and outside India and use global service providers, your information may be processed or stored in data centres located outside your country.",
      },
      {
        type: "p",
        text: "Where this happens, we take reasonable steps to ensure your data remains protected under this policy.",
      },
    ],
  },
  {
    id: "data-retention",
    title: "Data retention",
    number: "6",
    blocks: [
      {
        type: "p",
        text: "We retain your account and content for as long as your account is active.",
      },
      {
        type: "p",
        text: "If you delete your account or your subscription ends, we retain your personal data for up to 30 days, after which it is deleted, except where we are required to keep limited records (such as transaction and tax records) for legal, accounting, or fraud-prevention purposes.",
      },
    ],
  },
  {
    id: "marketing-emails",
    title: "Marketing emails",
    number: "7",
    blocks: [
      {
        type: "p",
        text: "We may send you marketing and promotional emails about AIGIO.",
      },
      {
        type: "p",
        text: "Every marketing email includes an unsubscribe link, and you can opt out at any time.",
      },
      {
        type: "p",
        text: "You will still receive essential service and transactional emails (for example, billing and security notices).",
      },
    ],
  },
  {
    id: "cookies",
    title: "Cookies",
    number: "8",
    blocks: [
      {
        type: "p",
        text: "We use essential, analytics, and marketing cookies. You can control non-essential cookies through your browser or our cookie controls.",
      },
      {
        type: "p",
        text: "For full details, see our Cookie Policy.",
      },
    ],
  },
  {
    id: "security",
    title: "Security",
    number: "9",
    blocks: [
      {
        type: "p",
        text: "We use reasonable technical and organisational measures to protect your data, including password hashing, access controls, and encrypted transport.",
      },
      {
        type: "p",
        text: "No method of transmission or storage is completely secure, so we cannot guarantee absolute security.",
      },
    ],
  },
  {
    id: "your-rights",
    title: "Your rights",
    number: "10",
    blocks: [
      {
        type: "list",
        items: [
          "Access, correct, or update your account information.",
          "Request deletion of your account and personal data.",
          "Withdraw consent or object to certain processing, including marketing, where applicable.",
          "Nominate another person to exercise your rights in the event of death or incapacity, as provided under applicable law.",
        ],
      },
      {
        type: "p",
        text: "To exercise any of these, email support@aigio.ai.",
      },
    ],
  },
  {
    id: "grievance-officer",
    title: "Grievance Officer",
    number: "11",
    blocks: [
      {
        type: "p",
        text: "In accordance with Indian law, you may contact our Grievance Officer for any complaints or concerns about your personal data or this policy:",
      },
      {
        type: "p",
        text: "Name: Grievance Officer",
      },
      {
        type: "p",
        text: "Email: support@aigio.ai",
      },
      {
        type: "p",
        text: "Entity: Orglife Private Limited, B-3/202, Lahabita, Nr. Aiyana Complex, Opp. Shukun Bungalows, Off S. G. Highway, Thaltej, Ahmedabad, Gujarat, India - 380054",
      },
      {
        type: "p",
        text: "We aim to acknowledge and address grievances within the timelines required by applicable law.",
      },
    ],
  },
  {
    id: "children",
    title: "Children",
    number: "12",
    blocks: [
      {
        type: "p",
        text: "The Service does not impose a strict age limit. If you are a minor under applicable law, you should use the Service only with the consent and supervision of a parent or legal guardian, who is responsible for your use and any payments.",
      },
    ],
  },
  {
    id: "changes",
    title: "Changes to this policy",
    number: "13",
    blocks: [
      {
        type: "p",
        text: 'We may update this Privacy Policy from time to time. The "Last updated" date above reflects the current version.',
      },
    ],
  },
  {
    id: "contact",
    title: "Contact",
    number: "14",
    blocks: [
      {
        type: "p",
        text: "For any privacy questions or requests, contact us at support@aigio.ai.",
      },
    ],
  },
];

function renderBlock(block: Block, key: number) {
  switch (block.type) {
    case "list":
      return (
        <ul
          key={key}
          className="legal-list"
          style={{ listStyleType: "disc", paddingLeft: "24px", margin: "14px 0" }}
        >
          {block.items.map((item, i) => (
            <li key={i} style={{ marginBottom: "10px", display: "list-item" }}>
              {item}
            </li>
          ))}
        </ul>
      );
    default:
      return (
        <p key={key} className="legal-paragraph">
          {block.text}
        </p>
      );
  }
}

const PrivacyPolicy: React.FC = () => {
  const [activeId, setActiveId] = React.useState(SECTIONS[0].id);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -65% 0px", threshold: 0 },
    );

    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <div className="page-shell page-shell--legal">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
        </Helmet>
        <Header />
    

        <main className="legal-page">
          <div className="legal-page__inner">
            <p className="legal-page__kicker">LEGAL DOCUMENTATION</p>
            <h1 className="legal-page__title">Privacy Policy</h1>
            <p className="legal-page__updated">
              Last updated: {LAST_UPDATED}. AIGIO is a product of Orglife Private Limited
            </p>

            <div className="legal-page__body">
              <aside className="legal-toc" aria-label="Table of contents">
                <ul className="legal-toc__list">
                  {SECTIONS.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className={`legal-toc__link ${
                          activeId === s.id ? "legal-toc__link--active" : ""
                        }`}
                      >
                        {s.number ? `${s.number}. ` : ""}{s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </aside>

              <div className="legal-content">
                {/* Upper Section Verbatim from Document */}
                <div style={{ marginBottom: "32px" }}>
                  <p className="legal-paragraph">
                    This Privacy Policy explains what information Orglife Private Limited, the company behind AIGIO, collects, how we use and protect it, and the choices you have.It is aligned with India's Digital Personal Data Protection Act, 2023 and the Information Technology Act and rules.
                  </p>
                  <p className="legal-paragraph">
                    By using the Service, you agree to this policy.
                  </p>
                </div>

                {/* Numbered Sections */}
                {SECTIONS.map((s) => (
                  <section key={s.id} id={s.id} className="legal-section">
                    <h2 className="legal-section__title">
                      {s.number ? `${s.number}. ` : ""}{s.title}
                    </h2>
                    {s.blocks.map((b, j) => renderBlock(b, j))}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;